import { defineStore } from "pinia";
import * as fb from "../firebase";
import { collection, doc, getDoc, getDocs, setDoc } from "firebase/firestore";
import { VIEW_MODE, ViewMode, hasAdminScope } from "@/utils/menuScope";

export interface UserFlags {
  _usernamePK: string;
  defaultView: ViewMode;
}

interface UserFlagsState {
  defaultView: ViewMode;
  hasAdminPermissions: boolean;
  loading: boolean;
  menuItemsCache: Array<any> | null;
}

export const useUserFlagsStore = defineStore("userFlags", {
  state: (): UserFlagsState => ({
    defaultView: VIEW_MODE.USER,
    hasAdminPermissions: false,
    loading: false,
    menuItemsCache: null,
  }),

  getters: {
    isAdminView: (state): boolean => state.defaultView === VIEW_MODE.ADMIN,
    canAccessAdminView: (state): boolean => state.hasAdminPermissions,
  },

  actions: {
    async loadUserFlags(username: string, userPermissions: string[]) {
      this.loading = true;
      try {
        // Check if user has admin permissions by looking for admin-only menu items
        this.hasAdminPermissions = await this.checkAdminPermissions(
          userPermissions
        );

        // If no admin permissions, always set to user view
        if (!this.hasAdminPermissions) {
          this.defaultView = VIEW_MODE.USER;
          this.loading = false;
          return;
        }

        // Load user flags from Firestore
        const userFlagsDoc = await getDoc(
          doc(fb.firestore, "users", username, "userSettings", "userFlags")
        );

        if (userFlagsDoc.exists()) {
          const data = userFlagsDoc.data() as UserFlags;
          this.defaultView = data.defaultView || VIEW_MODE.USER;
        } else {
          // Default to user view if no flags exist
          this.defaultView = VIEW_MODE.USER;
        }
      } catch (error) {
        console.error("Error loading user flags:", error);
        this.defaultView = VIEW_MODE.USER;
      } finally {
        this.loading = false;
      }
    },

    async saveUserFlags(username: string) {
      if (!this.hasAdminPermissions) {
        // Don't save if user doesn't have admin permissions
        return;
      }

      try {
        // Note: Document path uses original username (case-sensitive)
        // but _usernamePK field stores lowercase version for efficient queries
        const usernamePK = username.toLowerCase();
        const userFlags: UserFlags = {
          _usernamePK: usernamePK,
          defaultView: this.defaultView,
        };

        await setDoc(
          doc(fb.firestore, "users", username, "userSettings", "userFlags"),
          userFlags,
          { merge: true }
        );
      } catch (error) {
        console.error("Error saving user flags:", error);
      }
    },

    async toggleView(username: string) {
      if (!this.hasAdminPermissions) {
        // Can't toggle if user doesn't have admin permissions
        return;
      }

      this.defaultView =
        this.defaultView === VIEW_MODE.ADMIN ? VIEW_MODE.USER : VIEW_MODE.ADMIN;
      await this.saveUserFlags(username);
    },

    async checkAdminPermissions(userPermissions: string[]): Promise<boolean> {
      // Check if menu collection has any items with Scope: "admin"
      // that the user has permissions for
      // Uses cached menu items if available to avoid repeated Firebase queries
      // NOTE: Cache persists for entire session. Menu changes require page refresh.
      // This is acceptable as menu items rarely change and the performance gain
      // from caching outweighs the need for real-time menu updates.
      try {
        // Use cached menu items if available
        let menuItems = this.menuItemsCache;

        if (!menuItems) {
          // Fetch and cache menu items
          const menuSnapshot = await getDocs(collection(fb.firestore, "menu"));
          menuItems = menuSnapshot.docs.map((d) => d.data());
          this.menuItemsCache = menuItems;
        }

        // Early return on first admin permission match for optimal performance
        // For typical menu sizes (10-50 items), this sequential search is
        // efficient and simpler than maintaining a separate index structure
        for (const menuItem of menuItems) {
          const scope = menuItem.Scope;

          // Check if this is an admin-only item and user has permission
          if (hasAdminScope(scope) && userPermissions.includes(menuItem.Key)) {
            return true; // Early return for efficiency
          }
        }

        return false;
      } catch (error) {
        console.error("Error checking admin permissions:", error);
        return false;
      }
    },

    clearState() {
      this.defaultView = VIEW_MODE.USER;
      this.hasAdminPermissions = false;
      this.loading = false;
      this.menuItemsCache = null;
    },
  },
});
